import type { UserOrganization } from '@outpost/api-client';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Boxes, Plus, UsersRound } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { ErrorState, ListSkeleton } from '@/components/states';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/lib/auth';
import { allows } from '@/lib/roles';
import { useTeams } from '@/lib/teams';

/**
 * The organization's teams, as a way in to each.
 *
 * Everyone sees the list: a member who has been given a project through a team
 * should be able to find out which team that was, and who else is on it.
 */
export function Teams({ organization }: { organization: UserOrganization }) {
  const teams = useTeams(organization.id);
  const canEdit = allows(organization.role, 'admin');

  return (
    <Card>
      <CardHeader className="flex-row flex-wrap items-start justify-between gap-4 space-y-0">
        <div className="space-y-1.5">
          <CardTitle className="flex items-center gap-2 text-base">
            <UsersRound className="size-4" />
            Teams
          </CardTitle>
          <CardDescription>
            A team owns projects and has members, and everyone on it can see everything it owns. It
            is the way to give a group of people the same set of projects once instead of one at a
            time.
          </CardDescription>
        </div>
        {canEdit && <NewTeamDialog organization={organization} />}
      </CardHeader>
      <CardContent className="space-y-3">
        {teams.isLoading && <ListSkeleton rows={2} />}
        {teams.isError && <ErrorState error={teams.error} onRetry={() => void teams.refetch()} />}

        {teams.data?.length === 0 && (
          <p className="text-sm text-muted-foreground">
            No teams yet.{' '}
            {canEdit
              ? 'Create one for a group of people who look after the same projects.'
              : 'An admin can create one.'}
          </p>
        )}

        {teams.data?.map((team) => (
          <Link
            key={team.id}
            to={`/teams/${team.id}`}
            className="flex items-center gap-3 rounded-md border border-border px-3 py-2 transition-colors hover:border-input-hover hover:shadow-sm"
          >
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <span className="truncate text-sm font-medium">{team.name}</span>
                {team.is_member && <Badge variant="muted">you</Badge>}
              </div>
              <span className="block text-xs text-muted-foreground">
                {count(team.member_count, 'member')} · {count(team.project_count, 'project')}
              </span>
            </div>
            <Boxes className="size-4 text-muted-foreground" />
          </Link>
        ))}
      </CardContent>
    </Card>
  );
}

function count(value: number, noun: string) {
  return `${value} ${noun}${value === 1 ? '' : 's'}`;
}

function NewTeamDialog({ organization }: { organization: UserOrganization }) {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');

  const create = useMutation({
    mutationFn: (teamName: string) => client.createTeam(organization.id, teamName),
    onSuccess: () => {
      setOpen(false);
      setName('');
      void queryClient.invalidateQueries({ queryKey: ['teams', organization.id] });
    },
  });

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) create.reset();
      }}
    >
      <DialogTrigger asChild>
        <Button variant="outline" size="sm">
          <Plus />
          New team
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create a team</DialogTitle>
          <DialogDescription>
            A team starts empty. Add people to it, then give it the projects they look after.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-1.5">
          <Label htmlFor="team-name">Name</Label>
          <Input
            id="team-name"
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder="Payments"
          />
        </div>
        {create.error && (
          <p className="text-sm text-destructive">{(create.error as Error).message}</p>
        )}
        <DialogFooter>
          <Button onClick={() => create.mutate(name)} disabled={!name.trim() || create.isPending}>
            Create
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
